import { useState } from "react";
import { Phone, Mail, MapPin, Clock, Map } from "lucide-react";
import { site, addressLine, areas } from "@/config/site";
import { SEO, breadcrumbLd } from "@/lib/seo";
import { PageHero } from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { ContactForm } from "@/components/ContactForm";
import { trackEvent } from "@/lib/analytics";

const MAP_URL = `https://maps.google.com/maps?q=${encodeURIComponent(addressLine)}&t=&z=14&ie=UTF8&iwloc=&output=embed`;

export default function Contact() {
  const [mapLoaded, setMapLoaded] = useState(false);

  const cards = [
    { icon: Phone, label: "Call or text", value: site.phone, href: site.phoneHref, testid: "contact-phone-link", event: () => trackEvent("phone_click", { location: "contact" }) },
    { icon: Mail, label: "Email", value: site.email, href: site.emailHref, testid: "contact-email-link", event: () => trackEvent("email_click", { location: "contact" }) },
    { icon: Clock, label: "Hours", value: "Open 24/7 — call or message any time" },
    {
      icon: MapPin,
      label: "Address",
      value: site.showAddress ? addressLine : "Serving Delta, Ladner, Tsawwassen & Surrey, BC",
    },
  ];

  return (
    <>
      <SEO
        title="Contact Us | North Ember Painting — Delta, BC"
        description="Contact North Ember Painting in Delta, BC. Call or text +1 (778) 581-5457, email northemberpainting@gmail.com, or send a message any time. Serving Delta, Ladner, Tsawwassen and Surrey."
        path="/contact"
        jsonLd={[breadcrumbLd([{ name: "Home", path: "/" }, { name: "Contact Us", path: "/contact" }])]}
      />
      <PageHero
        eyebrow="Contact us"
        title="Let's talk about your project"
        intro="Call, text, email or send the form — whatever's easiest. We're available 24 hours a day, 7 days a week."
        image="neighbourhood"
      />

      <section className="py-16 md:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 gap-14 lg:grid-cols-5">
            <div className="lg:col-span-2">
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-1">
                {cards.map((c, i) => (
                  <Reveal key={c.label} delay={i * 60}>
                    <div className="flex items-start gap-4 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                      <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ember/10 text-ember">
                        <c.icon className="h-5 w-5" aria-hidden="true" />
                      </span>
                      <div className="min-w-0">
                        <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">{c.label}</h2>
                        {c.href ? (
                          <a
                            href={c.href}
                            data-testid={c.testid}
                            onClick={c.event}
                            className="mt-1 block break-words font-display text-lg font-semibold text-slate-900 transition-colors duration-200 hover:text-ember-dark"
                          >
                            {c.value}
                          </a>
                        ) : (
                          <p className="mt-1 font-display text-lg font-semibold text-slate-900">{c.value}</p>
                        )}
                      </div>
                    </div>
                  </Reveal>
                ))}
              </div>

              <Reveal delay={200}>
                <div className="mt-6 rounded-2xl border border-slate-200 bg-slate-50 p-6">
                  <h2 className="text-xs font-semibold uppercase tracking-[0.2em] text-slate-400">Service areas</h2>
                  <p className="mt-2 text-sm leading-relaxed text-slate-600">
                    {areas.map((a) => a.name).join(" · ")}, British Columbia
                  </p>
                </div>
              </Reveal>

              <Reveal delay={260}>
                <div className="mt-6 overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-sm" data-testid="map-container">
                  {mapLoaded ? (
                    <iframe
                      title={`Map showing ${addressLine}`}
                      src={MAP_URL}
                      className="h-72 w-full border-0"
                      loading="lazy"
                      referrerPolicy="no-referrer-when-downgrade"
                      data-testid="contact-map"
                    />
                  ) : (
                    <button
                      type="button"
                      data-testid="load-map-btn"
                      onClick={() => setMapLoaded(true)}
                      className="flex h-72 w-full flex-col items-center justify-center gap-3 bg-slate-100 text-slate-600 transition-colors duration-200 hover:bg-slate-200/70"
                    >
                      <Map className="h-8 w-8 text-ember" aria-hidden="true" />
                      <span className="text-sm font-semibold">Load interactive map</span>
                      <span className="max-w-xs text-xs text-slate-500">
                        Loads Google Maps only when you choose — nothing is shared until then.
                      </span>
                    </button>
                  )}
                </div>
              </Reveal>
            </div>

            <Reveal className="lg:col-span-3" delay={120}>
              <div className="rounded-2xl border border-slate-200 bg-white p-7 shadow-lg sm:p-10">
                <h2 className="font-display text-2xl font-semibold tracking-tight text-slate-900 sm:text-3xl">
                  Send us a message
                </h2>
                <p className="mt-2 text-sm leading-relaxed text-slate-500">
                  For a detailed quote, use the{" "}
                  <a href="/estimate" className="font-semibold text-ember-dark underline">estimate form</a> — it
                  asks the questions that help us price accurately.
                </p>
                <div className="mt-8">
                  <ContactForm />
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </>
  );
}
